/**
 * 统一存储管理器
 * 提供高性能、大容量、非阻塞的存储解决方案
 */

import localCloudService from './localCloudService'

class StorageManager {
  constructor() {
    this.memoryCache = new Map() // 内存缓存
    this.saveQueue = [] // 保存队列
    this.isSaving = false // 是否正在保存
    this.storageType = this.detectBestStorage()
    this.maxCacheSize = 50 // 内存缓存最多50个故事
    
    console.log(`📦 StorageManager 初始化完成，使用: ${this.storageType}`)
  }

  /**
   * 自动检测最佳存储方式
   */
  detectBestStorage() {
    if ('indexedDB' in window) {
      console.log('✅ 检测到 IndexedDB 支持')
      return 'indexedDB'
    }
    
    console.warn('⚠️ IndexedDB 不可用，使用 sessionStorage')
    return 'sessionStorage'
  }

  /**
   * 保存故事（异步，永不阻塞UI）
   * @param {Object} story - 故事对象
   * @returns {Promise<Object>} 故事对象
   */
  async saveStory(story) {
    try {
      // 1. 立即保存到内存缓存
      this.memoryCache.set(story.id, story)
      this.trimCache() // 限制缓存大小
      
      // 2. 异步保存到持久化存储（不等待）
      this.queueSave({
        type: 'story',
        id: story.id,
        data: story
      })
      
      // 3. 立即返回（不等待存储完成）
      return Promise.resolve(story)
    } catch (error) {
      console.error('❌ 保存故事到缓存失败:', error)
      // 即使缓存失败，也尝试队列保存
      this.queueSave({
        type: 'story',
        id: story.id,
        data: story
      })
      return Promise.resolve(story)
    }
  }

  /**
   * 加入保存队列
   * @param {Object} task - 保存任务
   */
  queueSave(task) {
    // 检查队列中是否已有相同ID的任务
    const existingIndex = this.saveQueue.findIndex(
      t => t.type === task.type && t.id === task.id
    )
    
    if (existingIndex >= 0) {
      // 更新现有任务
      this.saveQueue[existingIndex] = task
      console.log(`🔄 更新队列任务: ${task.type} ${task.id}`)
    } else {
      // 添加新任务
      this.saveQueue.push(task)
      console.log(`➕ 添加队列任务: ${task.type} ${task.id} (队列长度: ${this.saveQueue.length})`)
    }
    
    // 启动队列处理
    if (!this.isSaving) {
      this.processSaveQueue()
    }
  }

  /**
   * 处理保存队列
   */
  async processSaveQueue() {
    if (this.isSaving || this.saveQueue.length === 0) {
      return
    }

    this.isSaving = true
    console.log(`🔄 开始处理保存队列，共 ${this.saveQueue.length} 个任务`)

    while (this.saveQueue.length > 0) {
      const task = this.saveQueue.shift()
      
      try {
        await this.saveToStorage(task)
        console.log(`✅ 任务完成: ${task.type} ${task.id}`)
      } catch (error) {
        console.error(`❌ 任务失败: ${task.type} ${task.id}`, error)
        // 失败的任务重新加入队列（最多3次）
        task.retryCount = (task.retryCount || 0) + 1
        if (task.retryCount < 3) {
          this.saveQueue.push(task)
          console.log(`🔄 任务重新入队 (第${task.retryCount}次重试): ${task.id}`)
        } else {
          console.error(`❌ 任务失败3次，放弃: ${task.id}`)
        }
      }
      
      // 每个任务之间短暂延迟，避免阻塞
      await new Promise(resolve => setTimeout(resolve, 10))
    }

    this.isSaving = false
    console.log('✅ 保存队列处理完成')
  }

  /**
   * 保存到持久化存储
   * @param {Object} task - 保存任务
   */
  async saveToStorage(task) {
    if (this.storageType === 'indexedDB') {
      // 使用 IndexedDB
      await this.saveToIndexedDB(task)
    } else {
      // 使用 sessionStorage
      await this.saveToSessionStorage(task)
    }
  }

  /**
   * 保存到 IndexedDB
   * @param {Object} task - 保存任务
   */
  async saveToIndexedDB(task) {
    try {
      const key = `${task.type}-${task.id}`
      
      // 使用超时保护
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('IndexedDB 保存超时')), 10000)
      )
      
      await Promise.race([
        localCloudService.saveToFile(key, task.data),
        timeoutPromise
      ])
      
      console.log(`✅ IndexedDB 保存成功: ${key}`)
    } catch (error) {
      console.error('❌ IndexedDB 保存失败:', error)
      // 降级到 sessionStorage
      await this.saveToSessionStorage(task)
    }
  }

  /**
   * 保存到 sessionStorage
   * @param {Object} task - 保存任务
   */
  async saveToSessionStorage(task) {
    try {
      const key = `altverse-${task.type}-${task.id}`
      const dataString = JSON.stringify(task.data)
      
      // sessionStorage 容量检查
      if (dataString.length > 5 * 1024 * 1024) {
        console.warn('⚠️ 数据过大，sessionStorage 可能不足')
        // 尝试保存简化版本
        const simplified = this.simplifyData(task.data)
        sessionStorage.setItem(key, JSON.stringify(simplified))
        console.log(`✅ sessionStorage 保存成功（简化版）: ${key}`)
      } else {
        sessionStorage.setItem(key, dataString)
        console.log(`✅ sessionStorage 保存成功: ${key}`)
      }
    } catch (error) {
      if (error.name === 'QuotaExceededError') {
        console.warn('⚠️ sessionStorage 空间不足，清理旧数据...')
        this.cleanupSessionStorage()
        // 重试一次
        const key = `altverse-${task.type}-${task.id}`
        const simplified = this.simplifyData(task.data)
        sessionStorage.setItem(key, JSON.stringify(simplified))
      } else {
        throw error
      }
    }
  }

  /**
   * 从存储加载故事
   * @param {String} storyId - 故事ID
   * @returns {Promise<Object|null>} 故事对象
   */
  async loadStory(storyId) {
    try {
      // 1. 先从内存缓存读取
      if (this.memoryCache.has(storyId)) {
        console.log(`✅ 从内存缓存读取: ${storyId}`)
        return this.memoryCache.get(storyId)
      }

      // 2. 从 IndexedDB 读取
      if (this.storageType === 'indexedDB') {
        const key = `story-${storyId}`
        const story = await localCloudService.loadFromFile(key)
        if (story) {
          console.log(`✅ 从 IndexedDB 读取: ${storyId}`)
          this.memoryCache.set(storyId, story)
          return story
        }
      }

      // 3. 从 sessionStorage 读取
      const sessionKey = `altverse-story-${storyId}`
      const sessionData = sessionStorage.getItem(sessionKey)
      if (sessionData) {
        console.log(`✅ 从 sessionStorage 读取: ${storyId}`)
        const story = JSON.parse(sessionData)
        this.memoryCache.set(storyId, story)
        return story
      }

      console.warn(`⚠️ 未找到故事: ${storyId}`)
      return null
    } catch (error) {
      console.error('❌ 加载故事失败:', error)
      return null
    }
  }

  /**
   * 删除故事
   * @param {String} storyId - 故事ID
   */
  async deleteStory(storyId) {
    try {
      // 1. 从内存缓存删除
      this.memoryCache.delete(storyId)

      // 2. 从 IndexedDB 删除
      if (this.storageType === 'indexedDB') {
        const key = `story-${storyId}`
        await localCloudService.deleteFile(key)
      }

      // 3. 从 sessionStorage 删除
      const sessionKey = `altverse-story-${storyId}`
      sessionStorage.removeItem(sessionKey)

      console.log(`🗑️ 故事已删除: ${storyId}`)
    } catch (error) {
      console.error('❌ 删除故事失败:', error)
      throw error
    }
  }

  /**
   * 获取所有故事ID
   * @returns {Promise<Array<String>>} 故事ID数组
   */
  async getAllStoryIds() {
    const ids = new Set()

    // 从内存缓存
    this.memoryCache.forEach((_, id) => ids.add(id))

    // 从 sessionStorage
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i)
      if (key && key.startsWith('altverse-story-')) {
        const id = key.replace('altverse-story-', '')
        ids.add(id)
      }
    }

    return Array.from(ids)
  }

  /**
   * 限制缓存大小
   */
  trimCache() {
    if (this.memoryCache.size > this.maxCacheSize) {
      // 删除最旧的条目（简单的FIFO）
      const toDelete = this.memoryCache.size - this.maxCacheSize
      const keys = Array.from(this.memoryCache.keys())
      
      for (let i = 0; i < toDelete; i++) {
        this.memoryCache.delete(keys[i])
      }
      
      console.log(`🧹 缓存清理: 删除 ${toDelete} 个旧条目`)
    }
  }

  /**
   * 清理 sessionStorage
   */
  cleanupSessionStorage() {
    try {
      const keysToRemove = []
      
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i)
        if (key && !key.includes('altverse-')) {
          keysToRemove.push(key)
        }
      }
      
      keysToRemove.forEach(key => sessionStorage.removeItem(key))
      console.log(`🧹 sessionStorage 清理: 删除 ${keysToRemove.length} 个非相关项`)
    } catch (error) {
      console.error('❌ sessionStorage 清理失败:', error)
    }
  }

  /**
   * 简化数据（移除大型内容）
   * @param {Object} data - 原始数据
   * @returns {Object} 简化后的数据
   */
  simplifyData(data) {
    // 创建浅拷贝
    const simplified = { ...data }
    
    // 移除或简化大型字段
    if (simplified.nodes && Array.isArray(simplified.nodes)) {
      simplified.nodes = simplified.nodes.map(node => ({
        id: node.id,
        type: node.type,
        position: node.position,
        data: {
          title: node.data?.title || '',
          // 移除图片等大型数据
        }
      }))
    }
    
    // 移除元数据中的大型内容
    if (simplified.metadata) {
      delete simplified.metadata.chatHistory
      delete simplified.metadata.conversationHistory
    }
    
    console.log('📦 数据已简化')
    return simplified
  }

  /**
   * 获取存储统计信息
   * @returns {Object} 统计信息
   */
  getStats() {
    return {
      memoryCache: {
        size: this.memoryCache.size,
        maxSize: this.maxCacheSize
      },
      saveQueue: {
        length: this.saveQueue.length,
        isSaving: this.isSaving
      },
      storageType: this.storageType
    }
  }

  /**
   * 清空所有缓存
   */
  clearCache() {
    this.memoryCache.clear()
    console.log('🧹 内存缓存已清空')
  }
}

// 创建全局单例
const storageManager = new StorageManager()

export default storageManager



